/**
 * Outline schedule chip tooltip: the rule, plus the next local due date when the mount can date one.
 * A template outline has no calendar anchor — no plan effectiveFrom, no line start, no blackouts —
 * so it gets the rule alone rather than a fabricated next visit.
 */
import { DayShortName, ServicePlanBlackoutRange } from '@jms/model';
import { DUE_DAY_NO_SITE } from '@jms/model/entities/planned.work/service.plan/planned.task.due.day';
import { ScheduleContext, ScheduleEngine, ScheduleExceptions } from '@jms/model/scheduling';
import { ScheduleWeekday } from '@jms/model/scheduling/schedule.rule';
import { OutlineRow } from '../outline.authoring.model';

const ENGINE = new ScheduleEngine();
const NO_FURTHER_VISITS = 'No further visits';
const DEFAULT_WORK_WEEK: ScheduleWeekday[] = [1, 2, 3, 4, 5];

/** ISO weekdays (Sun = 7). Not `DayNumber` (Sun = 0). */
const ISO_WEEKDAY: Record<DayShortName, ScheduleWeekday> = {
  [DayShortName.Mon]: 1,
  [DayShortName.Tue]: 2,
  [DayShortName.Wed]: 3,
  [DayShortName.Thu]: 4,
  [DayShortName.Fri]: 5,
  [DayShortName.Sat]: 6,
  [DayShortName.Sun]: 7,
};

export type OutlineSchedulePlanContext = {
  timezone?: string;
  workingDays?: DayShortName[];
  blackoutRanges?: ServicePlanBlackoutRange[];
};

export type OutlineScheduleCard = {
  schedule: string;
  /** The task's own start, spelled out. Present only when the row overrides the line's start. */
  firstDue?: string;
  next?: string;
};

export function outlineWorkWeek(source: { workingDays?: DayShortName[] } | null | undefined): ScheduleWeekday[] {

  const mapped = [...new Set((source?.workingDays ?? []).map(day => ISO_WEEKDAY[day]).filter(Boolean))]
    .sort((left, right) => left - right);
  return mapped.length ? mapped : [...DEFAULT_WORK_WEEK];
}

/**
 * The `next` clause needs a real calendar to project onto. `perVisit` has never had one (it rides
 * the visit). A template outline has none either — `datesVisits: false` says the mount tracks no
 * calendar at all — so it stops at the rule rather than dating a visit that does not exist. The live
 * outline leaves the flag on and keeps counting from today even when a plan passes no context, which
 * is what it has always done.
 */
export function outlineScheduleCard(row: Partial<OutlineRow>, today: string, line: Partial<OutlineRow> | null | undefined, plan: OutlineSchedulePlanContext | null | undefined, datesVisits = true): OutlineScheduleCard {

  const schedule = ENGINE.describe(row.schedule);
  // A stored startsOn is always a genuine override (the API normalises redundant ones away), so the
  // chip's abbreviated clause earns a spelled-out counterpart here whatever the kind.
  const firstDue = row.startsOn ? { firstDue: formatLongDate(row.startsOn) } : {};
  if (!datesVisits || row.schedule?.kind === 'perVisit') return { schedule, ...firstDue };
  const next = ENGINE.nextAfter(row.schedule, addDays(today, -1), outlineScheduleContext(row, today, line, plan), outlineScheduleExceptions(row, line, plan));
  if (!next) return { schedule, ...firstDue, next: NO_FURTHER_VISITS };
  return { schedule, ...firstDue, next: `${formatChipDate(next)} · ${describeRelativeDue(next, today)}` };
}

export function describeRelativeDue(date: string, today: string): string {

  if (date === today) return 'today';
  if (date === addDays(today, 1)) return 'tomorrow';
  const days = calendarDayDelta(today, date);
  if (days > 0) return days === 1 ? 'in 1 day' : `in ${days} days`;
  return days === -1 ? 'yesterday' : `${Math.abs(days)} days ago`;
}

function outlineScheduleContext(row: Partial<OutlineRow>, today: string, line: Partial<OutlineRow> | null | undefined, plan: OutlineSchedulePlanContext | null | undefined): ScheduleContext {

  return {
    effectiveFrom: row.startsOn || line?.effectiveFrom || today,
    siteId: DUE_DAY_NO_SITE,
    timezone: line?.timezone || plan?.timezone || 'UTC',
    workWeek: line?.workingDays?.length ? outlineWorkWeek(line) : outlineWorkWeek(plan),
    today,
    lastCompleted: row.lastCompleted,
  };
}

function outlineScheduleExceptions(row: Partial<OutlineRow>, line: Partial<OutlineRow> | null | undefined, plan: OutlineSchedulePlanContext | null | undefined): ScheduleExceptions | undefined {

  const skips = row.scheduleExceptions?.skips;
  const extras = row.scheduleExceptions?.extras;
  const lineBlackouts = line?.blackoutRanges;
  const planBlackouts = plan?.blackoutRanges;
  if (!skips?.length && !extras?.length && !lineBlackouts?.length && !planBlackouts?.length) return undefined;
  return {
    ...(skips?.length ? { skips } : {}),
    ...(extras?.length ? { extras } : {}),
    ...(lineBlackouts?.length ? { lineBlackouts } : {}),
    ...(planBlackouts?.length ? { planBlackouts } : {}),
  };
}

function calendarDayDelta(from: string, to: string): number {

  const start = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const end = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((end - start) / 86_400_000);
}

function addDays(date: string, days: number): string {

  const delta = calendarDayDelta('1970-01-01', date) + days;
  const stamp = new Date(Date.UTC(1970, 0, 1 + delta));
  return stamp.toISOString().slice(0, 10);
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH_LABELS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/** The card has room the chip does not, so the override start is spelled out: '15 September 2026'. */
function formatLongDate(date: string): string {

  const month = MONTH_LABELS_LONG[Number(date.slice(5, 7)) - 1];
  if (!month) return date;
  return `${Number(date.slice(8, 10))} ${month} ${date.slice(0, 4)}`;
}

function formatChipDate(date: string): string {

  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7)) - 1;
  const day = Number(date.slice(8, 10));
  const weekday = (new Date(year, month, day).getDay() + 6) % 7;
  return `${WEEKDAY_LABELS[weekday]} ${day} ${MONTH_LABELS[month]}`;
}
